import { useQuery } from '@tanstack/react-query';
import { api } from '../../lib/api.js';
import { ResponsiveContainer, BarChart, Bar, Tooltip } from 'recharts';

function fmt(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}k`;
  return String(n);
}

function fmtMs(ms: number | undefined | null): string {
  if (!ms) return '—';
  if (ms >= 60_000) return `${(ms / 60_000).toFixed(1)}m`;
  if (ms >= 1_000) return `${(ms / 1_000).toFixed(1)}s`;
  return `${ms}ms`;
}

export function AiUsageWidget() {
  const { data } = useQuery({
    queryKey: ['ai-usage/stats'],
    queryFn: () => api.getAiUsageStats(),
    refetchInterval: 60_000,
  });

  const totals = data?.totals;
  const byModel = data?.byModel ?? [];
  const perDay = data?.perDay ?? [];
  const byContext = data?.byContext ?? [];

  return (
    <div
      className="glass rounded-2xl p-5 flex flex-col gap-4"
      style={{ boxShadow: '0 3px 20px rgba(0,0,0,0.3)' }}
    >
      <div className="text-xs font-semibold uppercase tracking-widest text-text-secondary">AI usage · last 7d</div>

      {/* Top-line stats */}
      <div className="grid grid-cols-3 gap-2">
        {[
          { label: 'Calls (24h)', value: fmt(totals?.calls24h ?? 0) },
          { label: 'Tokens out (24h)', value: fmt(totals?.tokensOut24h ?? 0) },
          { label: 'Avg latency', value: fmtMs(totals?.avgDuration24h) },
        ].map(({ label, value }) => (
          <div
            key={label}
            className="rounded-xl px-3 py-2 flex flex-col gap-0.5"
            style={{ background: 'var(--bg-tertiary)', border: '1px solid var(--glass-border)' }}
          >
            <div className="font-mono text-lg font-bold text-text-primary">{value}</div>
            <div className="text-[9px] uppercase tracking-widest text-text-dimmed">{label}</div>
          </div>
        ))}
      </div>

      {/* Sparkline — calls per day */}
      {perDay.length > 0 && (
        <div className="h-14">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={perDay} barCategoryGap={3}>
              <Bar dataKey="calls" fill="rgba(13,207,184,0.5)" radius={[2, 2, 0, 0]} />
              <Tooltip
                contentStyle={{ background: 'var(--bg-secondary)', border: '1px solid var(--glass-border)', borderRadius: 8, fontSize: 11 }}
                formatter={(v) => [`${fmt(Number(v))} calls`, '']}
                labelFormatter={(l) => String(l).slice(0, 10)}
              />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Per-model breakdown */}
      {byModel.length > 0 && (
        <div className="space-y-1.5">
          <div className="text-[9px] uppercase tracking-widest text-text-dimmed">By model</div>
          {byModel.slice(0, 4).map((row) => (
            <div key={row.model} className="flex items-center gap-2 text-xs">
              <span
                className="rounded px-1.5 py-0.5 text-[9px] font-mono font-semibold shrink-0"
                style={{ background: 'rgba(13,207,184,0.12)', color: 'var(--accent-blue)' }}
              >
                {row.provider}
              </span>
              <span className="flex-1 truncate text-text-secondary font-mono text-[10px]">{row.model.replace(/^.*\//, '')}</span>
              <span className="text-text-dimmed font-mono text-[10px] shrink-0">{fmt(row.calls)} calls</span>
              <span className="text-text-dimmed font-mono text-[10px] shrink-0">{fmt(row.tokensOut)} tok</span>
            </div>
          ))}
        </div>
      )}

      {/* Per-context breakdown */}
      {byContext.filter((r) => r.context).length > 0 && (
        <div className="space-y-1">
          <div className="text-[9px] uppercase tracking-widest text-text-dimmed">By prompt</div>
          {byContext.filter((r) => r.context).slice(0, 5).map((row) => (
            <div key={row.context ?? 'null'} className="flex items-center gap-2 text-[10px]">
              <span className="flex-1 truncate font-mono text-text-dimmed">{row.context}</span>
              <span className="text-text-dimmed shrink-0">{fmt(row.calls)}×</span>
            </div>
          ))}
        </div>
      )}

      {byModel.length === 0 && (
        <div className="text-xs text-text-dimmed text-center py-4">No AI calls recorded yet</div>
      )}
    </div>
  );
}
